import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Shield, Building2, Users, Phone, PhoneCall, Lock, MessageSquarePlus, ChevronRight, Loader2 } from 'lucide-react';
import { PaginaKop } from '../components/ZijMenu';
import { SupportChatPanel } from '../components/SupportChatPanel';
import { chatAPI, type Conversation } from '../services/chatService';
import { werkplekApi } from '../services/professionalWerkplekApi';

/** Centrale meldkamer: telefonische escalatielijn naast de supportchat. */
const BELAGOM_SUPPORT_TEL = '+31857609034';
const BELAGOM_SUPPORT_WEERGAVE = '085-7609034';

type Kanaal = 'support' | 'opdrachtgevers' | 'professionals';

const KANAAL_TYPES: Record<Exclude<Kanaal, 'support'>, Conversation['type'][]> = {
  opdrachtgevers: ['professional_employer'],
  professionals: ['professional_peer', 'professional_group'],
};

const KANAAL_TAB: Record<Exclude<Kanaal, 'support'>, string> = {
  opdrachtgevers: 'opdrachtgever',
  professionals: 'professionals',
};

const GesprekkenLijst: React.FC<{ kanaal: Exclude<Kanaal, 'support'>; gesprekken: Conversation[]; laden: boolean }> = ({
  kanaal,
  gesprekken,
  laden,
}) => {
  const { t } = useTranslation('professional');
  const lijst = gesprekken.filter((c) => KANAAL_TYPES[kanaal].includes(c.type));
  const naam = (c: Conversation) =>
    c.group_name || [c.participant?.first_name, c.participant?.last_name].filter(Boolean).join(' ') || c.onderwerp || t('werkplek.contact.gesprek');

  return (
    <div className="flex flex-col h-full min-h-0">
      {kanaal === 'opdrachtgevers' && (
        <div className="flex gap-2 items-start px-4 py-3 bg-gold-50 border-b border-gold-200 text-xs text-navy-800">
          <Lock className="w-4 h-4 flex-shrink-0 text-gold-700 mt-0.5" />
          {t('werkplek.contact.privacyGrendel')}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {laden ? (
          <div className="flex items-center justify-center py-10 text-navy-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : lijst.length === 0 ? (
          <p className="text-sm text-navy-400 text-center py-10">{t(`werkplek.contact.geen.${kanaal}`)}</p>
        ) : (
          lijst.map((c) => (
            <Link
              key={c.id}
              to={`/berichten?tab=${KANAAL_TAB[kanaal]}&conversation=${c.id}`}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-navy-50"
            >
              <span className="flex-1 min-w-0">
                <span className={`block text-sm truncate ${c.unread_count ? 'font-semibold text-navy-900' : 'text-navy-800'}`}>{naam(c)}</span>
                {(c.last_message || c.onderwerp) && <span className="block text-xs text-navy-400 truncate">{c.last_message || c.onderwerp}</span>}
              </span>
              {(c.unread_count ?? 0) > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gold-400 text-navy-900 text-xs font-bold">
                  {c.unread_count}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-navy-300" />
            </Link>
          ))
        )}
      </div>
      <div className="border-t border-gray-100 p-3 flex flex-wrap gap-2">
        <Link
          to={`/berichten?tab=${KANAAL_TAB[kanaal]}`}
          className="inline-flex items-center gap-2 rounded-lg bg-navy-900 px-3 py-2 text-sm font-semibold text-white hover:bg-navy-800"
        >
          {t('werkplek.contact.openBerichten')}
        </Link>
        {kanaal === 'professionals' && (
          <Link
            to="/professionals/chat"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-navy-800 hover:bg-navy-50"
          >
            <MessageSquarePlus className="w-4 h-4" />
            {t('werkplek.contact.nieuweCollegaChat')}
          </Link>
        )}
      </div>
    </div>
  );
};

/**
 * Hoofdmenu Contact (Menu 7): links de drievoudige chatmatrix (support,
 * opdrachtgevers, collega's), rechts de dubbele telefonische escalatielijn.
 */
const ProfessionalContactPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const [kanaal, setKanaal] = useState<Kanaal>('support');
  const [companyId, setCompanyId] = useState('');

  const gesprekken = useQuery({ queryKey: ['conversations'], queryFn: chatAPI.getConversations, refetchInterval: 30_000 });
  const opdrachtgevers = useQuery({ queryKey: ['werkplek-opdrachtgevers'], queryFn: werkplekApi.opdrachtgevers });
  const contact = useQuery({
    queryKey: ['werkplek-opdrachtgever-contact', companyId],
    queryFn: () => werkplekApi.opdrachtgeverContact(companyId),
    enabled: !!companyId,
    retry: false,
  });

  const ongelezen = (types: Conversation['type'][]) =>
    (gesprekken.data ?? []).filter((c) => types.includes(c.type)).reduce((som, c) => som + (c.unread_count || 0), 0);

  const kanalen: Array<{ key: Kanaal; icon: React.ComponentType<{ className?: string }>; aantal: number }> = [
    { key: 'support', icon: Shield, aantal: ongelezen(['professional_belagom', 'admin_broadcast']) },
    { key: 'opdrachtgevers', icon: Building2, aantal: ongelezen(KANAAL_TYPES.opdrachtgevers) },
    { key: 'professionals', icon: Users, aantal: ongelezen(KANAAL_TYPES.professionals) },
  ];

  return (
    <div className="bg-gray-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <PaginaKop titel={t('werkplek.contact.titel')} ondertitel={t('werkplek.contact.ondertitel')} />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_22rem] gap-5">
          {/* Drievoudige chatmatrix */}
          <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col h-[36rem]">
            <div className="grid grid-cols-3 border-b border-gray-200" role="tablist">
              {kanalen.map(({ key, icon: Icon, aantal }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={kanaal === key}
                  onClick={() => setKanaal(key)}
                  className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 px-2 py-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors ${
                    kanaal === key ? 'border-gold-500 text-navy-900 bg-gold-50' : 'border-transparent text-navy-500 hover:text-navy-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-center">{t(`werkplek.contact.kanaal.${key}`)}</span>
                  {aantal > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-gold-400 text-navy-900 text-[11px] font-bold">
                      {aantal}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex-1 min-h-0">
              {kanaal === 'support' ? (
                <SupportChatPanel />
              ) : (
                <GesprekkenLijst kanaal={kanaal} gesprekken={gesprekken.data ?? []} laden={gesprekken.isLoading} />
              )}
            </div>
          </section>

          {/* Dubbele telefonische escalatielijn */}
          <aside className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-navy-900">{t('werkplek.contact.belSupportTitel')}</h2>
              <p className="text-xs text-navy-500 mt-1">{t('werkplek.contact.belSupportTekst')}</p>
              <a
                href={`tel:${BELAGOM_SUPPORT_TEL}`}
                className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700"
              >
                <PhoneCall className="w-4 h-4" />
                {t('werkplek.contact.belSupportKnop', { nummer: BELAGOM_SUPPORT_WEERGAVE })}
              </a>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-navy-900">{t('werkplek.contact.belOpdrachtgeverTitel')}</h2>
              <p className="text-xs text-navy-500 mt-1">{t('werkplek.contact.belOpdrachtgeverTekst')}</p>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                disabled={opdrachtgevers.isLoading || (opdrachtgevers.data?.length ?? 0) === 0}
                className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-navy-900 focus:border-navy-500 focus:ring-1 focus:ring-navy-500 disabled:bg-gray-50"
              >
                <option value="">
                  {(opdrachtgevers.data?.length ?? 0) === 0 && !opdrachtgevers.isLoading
                    ? t('werkplek.contact.geenKoppelingen')
                    : t('werkplek.contact.kiesOrganisatie')}
                </option>
                {(opdrachtgevers.data ?? []).map((o) => (
                  <option key={o.company_id} value={o.company_id}>
                    {o.organisatie}
                  </option>
                ))}
              </select>

              {companyId && contact.isLoading && (
                <div className="mt-3 flex items-center gap-2 text-sm text-navy-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('werkplek.contact.nummerOphalen')}
                </div>
              )}
              {companyId && contact.isError && <p className="mt-3 text-sm text-red-600">{t('werkplek.contact.nummerFout')}</p>}
              {companyId && contact.data && (
                <div className="mt-3">
                  {contact.data.contactpersoon && (
                    <p className="text-xs text-navy-500 mb-2">
                      {t('werkplek.contact.contactpersoon', { naam: contact.data.contactpersoon })}
                    </p>
                  )}
                  {contact.data.telefoon ? (
                    <a
                      href={`tel:${contact.data.telefoon.replace(/[^\d+]/g, '')}`}
                      className="flex items-center justify-center gap-2 rounded-lg bg-navy-900 px-4 py-3 text-sm font-semibold text-white hover:bg-navy-800"
                    >
                      <Phone className="w-4 h-4" />
                      {t('werkplek.contact.belOpdrachtgeverKnop', { nummer: contact.data.telefoon })}
                    </a>
                  ) : (
                    <p className="text-sm text-navy-500">{t('werkplek.contact.geenNummer')}</p>
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalContactPage;
